/**
 * Pending-submissions queue. The PWA writes form submissions here
 * FIRST and lets the sync engine drain. Source of truth for offline
 * writes — TanStack Query mutation persistence is intentionally NOT
 * used (it conflicts with our Dexie-as-truth model).
 *
 * Wire format
 * - client_submission_id: crypto.randomUUID(), generated at queue time
 *   and used as the row PK. Same UUID survives retries; backend dedups.
 * - endpoint: relative path to POST against (e.g.
 *   /api/v1/data-vault/sampling/events/{id}/field-records).
 * - payload: the request body the engine will JSON.stringify and POST.
 *
 * Photos and other Blobs are stored separately in photo_blobs (so
 * pending_submissions stays compact and indexable by status).
 */
import type { PendingSubmission } from '../db';
import { getDb } from '../db';
import { logEvent } from './log';
import { triggerBackgroundSync, kickEngine } from './engine';

export interface QueueOpts {
  endpoint: string;
  payload: unknown;
  /** event_id for plan-aware writes; null for ad-hoc captures. */
  event_id?: string | null;
}

export async function enqueueSubmission(opts: QueueOpts): Promise<string> {
  const db = getDb();
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const row: PendingSubmission = {
    client_submission_id: id,
    endpoint: opts.endpoint,
    payload: opts.payload,
    status: 'queued',
    event_id: opts.event_id ?? null,
    attempts: 0,
    last_error: null,
    last_attempt_at: null,
    created_at: Date.now(),
  };
  await db.pending_submissions.put(row);
  await logEvent('submit.queued', {
    client_submission_id: id,
    endpoint: opts.endpoint,
    event_id: row.event_id,
  });

  // Register Background Sync (Android Chrome) and kick the foreground
  // engine so the next visible tick attempts the submit immediately.
  void triggerBackgroundSync();
  kickEngine();
  return id;
}

/** Drop a row that the user explicitly cancels (swipe-to-delete on a
 *  queued row, or the "discard" button in a draft). */
export async function discardSubmission(client_submission_id: string): Promise<void> {
  await getDb().pending_submissions.delete(client_submission_id);
  await logEvent('submit.discarded', { client_submission_id });
}

/** Diagnostics — used by the SyncStatusBar. */
export async function queueDepth(): Promise<number> {
  return getDb()
    .pending_submissions.where('status')
    .anyOf('queued', 'syncing')
    .count();
}

export async function failedCount(): Promise<number> {
  return getDb().pending_submissions.where('status').equals('failed').count();
}
